import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Badge } from '../../core/Badge/Badge.js';
import { Button } from '../../core/Button/Button.js';
import { Group } from '../../core/Group/Group.js';
import { Stack } from '../../core/Stack/Stack.js';
import { Text } from '../../core/Text/Text.js';
import { Select } from '../../inputs/Select/Select.js';
import { TextInput } from '../../inputs/TextInput/TextInput.js';
import { Drawer, type DrawerProps } from '../../overlays/Drawer/Drawer.js';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterField {
  /** Unique key used in the resulting filter object. */
  key: string;
  /** Label rendered above the select. */
  label: ReactNode;
  /** Available options. */
  options: FilterOption[];
  /** Placeholder when nothing is selected. */
  placeholder?: string;
}

export type FilterValues = Record<string, string>;

export interface FilterDrawerSearch {
  /** Current (committed) query, owned by the consumer. */
  value: string;
  /**
   * Fires with the debounced query while the user types (sdk-utils
   * `useDebounce`), and immediately with `''` when the user clears.
   */
  onChange: (query: string) => void;
  placeholder?: string;
  label?: ReactNode;
  /** Debounce window in ms before `onChange` fires. Default `250`. */
  debounceMs?: number;
}

export interface FilterDrawerLabels {
  apply?: ReactNode;
  cancel?: ReactNode;
  clear?: ReactNode;
  /** Label of the implicit "no filter" option prepended to every select. */
  any?: string;
}

export interface FilterDrawerProps {
  /** Trigger label, defaults to "Filters". */
  triggerLabel?: ReactNode;
  /** Filter dimensions. */
  fields: FilterField[];
  /** Current applied values. */
  values: FilterValues;
  /** Fires when the user clicks Apply. */
  onApply: (next: FilterValues) => void;
  /** Optional reset hook fired by the Clear button. */
  onClear?: () => void;
  /** Drawer title. */
  title?: ReactNode;
  /** Helper copy rendered at the top of the drawer body. */
  description?: ReactNode;
  /** Optional free-text search rendered above the fields; debounced. */
  search?: FilterDrawerSearch;
  /** Drawer width. Default `sm`. */
  size?: DrawerProps['size'];
  /** Button / option copy overrides (i18n). */
  labels?: FilterDrawerLabels;
}

/**
 * "Filters" button + slide-out drawer with a stack of dropdowns. The trigger
 * shows a count badge of how many dimensions are currently active so users
 * see at a glance whether anything's filtering.
 *
 * Select changes live in a draft and only commit on Apply. The optional
 * `search` input is the exception: it streams through sdk-utils'
 * `useDebounce` so the list behind the drawer narrows as the user types
 * without a re-render per keystroke.
 */
export function FilterDrawer({
  triggerLabel = 'Filters',
  fields,
  values,
  onApply,
  onClear,
  title = 'Filter results',
  description = 'Apply one or more filters. Combinations narrow the results.',
  search,
  size = 'sm',
  labels
}: FilterDrawerProps): ReactNode {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<FilterValues>(values);

  // ── Debounced search ──────────────────────────────────────────────────
  const [searchDraft, setSearchDraft] = useState(search?.value ?? '');
  const debouncedSearch = useDebounce(searchDraft, search?.debounceMs ?? 250);
  const lastEmitted = useRef<string | null>(null);
  const searchOnChange = search?.onChange;
  const searchValue = search?.value;

  useEffect(() => {
    if (!searchOnChange || searchValue === undefined) return;
    if (debouncedSearch === searchValue) return;
    lastEmitted.current = debouncedSearch;
    searchOnChange(debouncedSearch);
    // Only the debounced value drives emission: the consumer's `value`
    // is compared, not depended on, so an external reset can't re-emit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (searchValue === undefined) return;
    // Echo of our own emission; leave whatever the user has typed since.
    if (searchValue === lastEmitted.current) return;
    setSearchDraft(searchValue);
  }, [searchValue]);

  const activeCount =
    Object.values(values).filter((v) => v && v !== 'all').length + (searchValue?.trim() ? 1 : 0);

  function openDrawer() {
    setDraft(values);
    setOpened(true);
  }
  function apply() {
    onApply(draft);
    setOpened(false);
  }
  function clear() {
    setDraft({});
    setSearchDraft('');
    if (searchOnChange && searchValue) {
      lastEmitted.current = '';
      searchOnChange('');
    }
    onClear?.();
    onApply({});
    setOpened(false);
  }

  return (
    <>
      <Button
        size="xs"
        variant="outline"
        leftSection={<FilterIcon />}
        rightSection={
          activeCount > 0 ? (
            <Badge size="xs" color="orange" variant="filled">
              {activeCount}
            </Badge>
          ) : undefined
        }
        onClick={openDrawer}
      >
        {triggerLabel}
      </Button>
      <Drawer opened={opened} onClose={() => setOpened(false)} title={title} position="right" size={size}>
        <Stack gap="md" style={{ padding: 4 }}>
          {description ? (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {description}
            </Text>
          ) : null}
          {search ? (
            <Stack gap={4}>
              <Text role="inputLabel">{search.label ?? 'Search'}</Text>
              <TextInput
                type="search"
                size="sm"
                placeholder={search.placeholder ?? 'Search…'}
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.currentTarget.value)}
                aria-label={typeof search.label === 'string' ? search.label : 'Search'}
              />
            </Stack>
          ) : null}
          {fields.map((field) => (
            <Stack key={field.key} gap={4}>
              <Text role="inputLabel">{field.label}</Text>
              <Select
                size="sm"
                placeholder={field.placeholder ?? labels?.any ?? 'Any'}
                value={draft[field.key] ?? ''}
                onChange={(e) =>
                  setDraft((cur) => {
                    const next = { ...cur };
                    const v = e.currentTarget.value;
                    if (v) next[field.key] = v;
                    else delete next[field.key];
                    return next;
                  })
                }
                data={[{ value: '', label: labels?.any ?? 'Any' }, ...field.options]}
              />
            </Stack>
          ))}
          <Group justify="space-between" style={{ paddingTop: 16, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button size="sm" variant="subtle" onClick={clear}>
              {labels?.clear ?? 'Clear all'}
            </Button>
            <Group gap="xs">
              <Button size="sm" variant="default" onClick={() => setOpened(false)}>
                {labels?.cancel ?? 'Cancel'}
              </Button>
              <Button size="sm" onClick={apply}>
                {labels?.apply ?? 'Apply'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Drawer>
    </>
  );
}

function FilterIcon(): ReactNode {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="2,3 12,3 8.5,7 8.5,11 5.5,11 5.5,7" />
    </svg>
  );
}
